import * as styles from "./ContractDocument.css";

import { useEffect, useMemo, useState } from "react";
import type { JSX, ReactNode } from "react";

import { contractApi } from "@frontend/features/contracts";
import type {
  Contract,
  ContractAmendment,
  ContractOptions,
} from "@frontend/features/contracts";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import type { Reference } from "@frontend/lib/references";
import { formatAmount, parseAmount } from "@frontend/utils/amount";
import { usePrintDocument } from "@frontend/utils/printDocument";
import { Trans, useLingui } from "@lingui/react/macro";

import { projectApi } from "../services";
import type { Project } from "../types";

export type ContractDocumentProps = {
  projectId: number;
  contractId: number;
  brand?: string;
};

type Loaded = {
  project: Project;
  contract: Contract;
  options: ContractOptions;
};

function money(value: string | null, locale: string): string {
  return null === value
    ? "-"
    : `${formatAmount(parseAmount(value) ?? 0, locale)} CHF`;
}

function day(value: string | null, locale: string): string {
  if (null === value) {
    return "-";
  }

  const date = new Date(`${value}T00:00:00`);

  return Number.isNaN(date.getTime())
    ? "-"
    : new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <tr>
      <th>{label}</th>
      <td>{children}</td>
    </tr>
  );
}

function Signed({ value }: { value: string }): JSX.Element {
  const locale = useIntlLocale();

  const diff = parseAmount(value) ?? 0;

  return (
    <span className={0 <= diff ? styles.up : styles.down}>
      {0 <= diff ? "+" : "-"} {money(String(Math.abs(diff)), locale)}
    </span>
  );
}

function AmendmentRow({
  amendment,
}: {
  amendment: ContractAmendment;
}): JSX.Element {
  const locale = useIntlLocale();

  const date = day(amendment.modificationDate, locale);
  const user = amendment.modificationUsername ?? "";

  return (
    <tr>
      <td>{day(amendment.dateBegin, locale)}</td>
      <td className={styles.amount}>{money(amendment.price, locale)}</td>
      <td className={styles.amount}>
        <Signed value={amendment.priceDiff} />
      </td>
      <td>
        {amendment.comments}

        {amendment.modificationDate ? (
          <div className={styles.muted}>
            <Trans>
              Modified on {date} by {user}
            </Trans>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

export function ContractDocument({
  projectId,
  contractId,
  brand,
}: ContractDocumentProps): JSX.Element | null {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const controller = new AbortController();
    const options = { signal: controller.signal };

    Promise.all([
      projectApi.fetchProject(projectId, options),
      contractApi.fetchContract(projectId, contractId, options),
      contractApi.fetchOptions(projectId, options),
    ]).then(
      ([project, contract, contractOptions]) =>
        setLoaded({ project, contract, options: contractOptions }),
      (reason: unknown) => {
        if (!controller.signal.aborted) {
          setError(reason);
        }
      },
    );

    return () => controller.abort();
  }, [projectId, contractId]);

  usePrintDocument(null !== loaded, error);

  // The option lists carry the labels the contract only holds ids of.
  const labels = useMemo(() => {
    const byId = (references: Reference[] | undefined) =>
      new Map((references ?? []).map((ref) => [ref.id, ref.label]));

    return {
      groups: byId(loaded?.options.contractGroups),
      billPeriods: byId(loaded?.options.billPeriods),
      terminationReasons: byId(loaded?.options.terminationReasons),
      priceTypes: new Map(
        (loaded?.options.priceTypes ?? []).map((type) => [
          type.value,
          type.label,
        ]),
      ),
    };
  }, [loaded]);

  if (null === loaded) {
    return null;
  }

  const { project, contract } = loaded;
  const condition = contract.commercialCondition;
  const version = contract.version;

  const yesNo = (value: boolean): string => (value ? t`Yes` : t`No`);

  const priceType = contract.priceType
    ? (labels.priceTypes.get(contract.priceType) ?? contract.priceType)
    : null;

  const printed = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date());

  return (
    <article className={styles.document}>
      <header className={styles.header}>
        <div>
          {brand ? <div className={styles.brand}>{brand}</div> : null}

          <h1 className={styles.title}>
            <Trans>Contract {version}</Trans>

            {project.label ? (
              <small className={styles.subtitle}>{project.label}</small>
            ) : null}
          </h1>
        </div>

        <div className={styles.meta}>
          {project.clientLabel ? <div>{project.clientLabel}</div> : null}
        </div>
      </header>

      <div className={styles.columns}>
        <section>
          <h2 className={styles.heading}>
            <Trans>Contract</Trans>
          </h2>

          <table className={styles.fields}>
            <tbody>
              <Field label={t`Version`}>{contract.version}</Field>
              <Field label={t`From`}>{day(contract.dateBegin, locale)}</Field>
              <Field label={t`To`}>{day(contract.realEndDate, locale)}</Field>
              <Field label={t`Duration`}>
                {contract.contractDurationLabel ?? "-"}
              </Field>
              <Field label={t`Notice period`}>
                {contract.endDelayLabel ?? "-"}
              </Field>
              <Field label={t`Group`}>
                {(null !== contract.contractGroupId &&
                  labels.groups.get(contract.contractGroupId)) ||
                  "-"}
              </Field>
              <Field label={t`Automatic renewal`}>
                {yesNo(contract.autoRenewal)}
              </Field>
              <Field label={t`Renewal warning`}>
                {yesNo(contract.renewalWarning)}
              </Field>
              {null !== contract.terminationReasonId ? (
                <Field label={t`Termination reason`}>
                  {labels.terminationReasons.get(
                    contract.terminationReasonId,
                  ) ?? "-"}
                </Field>
              ) : null}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className={styles.heading}>
            <Trans>Price</Trans>
          </h2>

          <table className={styles.fields}>
            <tbody>
              {null !== contract.price ? (
                <Field label={t`Original price`}>
                  {money(contract.price, locale)}
                  {priceType ? ` (${priceType})` : ""}
                </Field>
              ) : null}
              <Field label={t`Current price`}>
                <span className={styles.strong}>
                  {money(contract.priceAtDate, locale)}
                </span>

                {0 < contract.amendments.length ? (
                  <>
                    {" "}
                    (<Signed value={contract.priceDiff} />)
                  </>
                ) : null}
              </Field>
              <Field label={t`Billing period`}>
                {(null !== contract.billPeriodId &&
                  labels.billPeriods.get(contract.billPeriodId)) ||
                  "-"}
              </Field>
              <Field label={t`Billed after the period`}>
                {yesNo(contract.postPeriodBilling)}
              </Field>
              {condition ? (
                <>
                  <Field label={t`Payment period`}>
                    {condition.subscriptionPaymentPeriodLabel ?? "-"}
                  </Field>
                  <Field label={t`Payment type`}>
                    {condition.paymentTypeLabel ?? "-"}
                  </Field>
                  <Field label={t`Billing time`}>
                    {condition.subscriptionBillingTimeLabel ?? "-"}
                  </Field>
                </>
              ) : null}
            </tbody>
          </table>
        </section>
      </div>

      <section>
        <h2 className={styles.heading}>
          <Trans>Amendments</Trans>
        </h2>

        {0 < contract.amendments.length ? (
          <table className={styles.list}>
            <thead>
              <tr>
                <th>{t`From`}</th>
                <th className={styles.amount}>{t`Price`}</th>
                <th className={styles.amount} />
                <th>{t`Comments`}</th>
              </tr>
            </thead>

            <tbody>
              {contract.amendments.map((amendment, index) => (
                <AmendmentRow
                  key={amendment.id ?? index}
                  amendment={amendment}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <p className={styles.empty}>
            <Trans>This contract has no amendment.</Trans>
          </p>
        )}
      </section>

      <footer className={styles.footer}>
        {brand ? `${brand} · ` : ""}
        {printed}
      </footer>
    </article>
  );
}
